import { useWindowMessage } from '#imports';
import { SetAudioEnabled, UnlockAudio } from '@fanatics/resn-api/instant-rips';
import { noop } from '@vueuse/core';
import { Howl, Howler } from 'dm-howler';
import { defineComponent, inject, onMounted, onUnmounted, provide, ref, watchEffect } from 'vue';

import { gsap } from '@resn/gsap';

import { useParentSearchParams } from '~/components/useParentSearchParams';
import { instantRipsSoundDefs } from '~/core/audio';
import { createLogger } from '~/utils/logger';

const AudioKey = 'audio';
const logger = createLogger('audio');
const AMBIENT_SOUND_IDS = new Set(['cardStaticState', 'goldenTicketAmbient']);

const buildSounds = (soundDefs, createSoundFn, { onunlock = noop } = {}) =>
    Object.fromEntries(
        Object.entries(soundDefs).map(([id, { src, loop = false, volume = 1 }]) => [
            id,
            createSoundFn(src, { onunlock, loop, volume }),
        ])
    );

export const useAudio = () => {
    const env = inject(AudioKey);
    if (!env) {
        throw new Error('useAudio must be used within a AudioProvider');
    }
    return env;
};

const createSound = (src, { onunlock = noop, loop = false, volume = 1, rate = 1 } = {}) => {
    const sound = new Howl({ src: [src], loop, onunlock, volume, rate });
    sound.baseVolume = volume;
    sound.baseRate = rate;

    return sound;
};

const createLoopSound = (src, { start = 0, end = 0, volume = 1 } = {}) => {
    return new Howl({
        src: [src],
        loop: true,
        volume,
        sprite: [{ loop: [start, end, true] }],
    });
};

const AudioProvider = defineComponent({
    props: {
        soundDefs: { type: Object, default: () => instantRipsSoundDefs },
    },
    setup(props) {
        // Disable auto unlocking
        Howler.autoUnlock = false;

        const MAX_VOLUME = 0.5;

        const muted = ref(false);
        const volume = ref(0);
        const unlocked = ref(Howler.state === 'running');
        const disableAmbientAudio = ref(false);
        const tabActive = ref(true);

        let interval = null;

        // Handle visibility change events
        const handleVisibilityChange = () => {
            tabActive.value = !document.hidden;

            if (document.hidden) {
                // Tab lost focus - pause all audio
                pauseAllAudio();
            } else {
                // Tab gained focus - resume the audio context but not individual sounds
                resumeAudioContext();
            }
        };

        // Pause all currently playing audio
        const pauseAllAudio = () => {
            if (!unlocked.value || !sounds) return;
            // Susped the audio context
            if (Howler.ctx && Howler.ctx.state === 'running') Howler.ctx.suspend();
        };

        // Resume just the audio context, not individual sounds
        const resumeAudioContext = () => {
            if (!unlocked.value) return;
            // Resume the audio context
            if (Howler.ctx && Howler.ctx.state === 'suspended') Howler.ctx.resume();
        };

        useParentSearchParams((params) => {
            if (params.get('disableAmbientAudio') === 'true') {
                disableAmbientAudio.value = true;
            }
        });

        // Function to manually unlock audio
        const unlockAudio = (fromClick = false) => {
            if (unlocked.value) return;
            // console.log('Attempt unlockAudio', Howler .ctx.state);

            if (Howler.ctx) {
                if (Howler.ctx.state == 'running') {
                    onUnlockAudio();
                } else if (fromClick) {
                    Howler.ctx.resume().then(() => {
                        onUnlockAudio();
                    });
                }
            }
        };

        const onUnlockAudio = () => {
            unlocked.value = true;
            clearInterval(interval);
            // console.log('Audio context unlocked successfully');

            gsap.to(volume, { value: MAX_VOLUME, duration: 3 });
        };

        const sounds = buildSounds(props.soundDefs, createSound, { onunlock: onUnlockAudio });

        const isAmbientSound = (id) => AMBIENT_SOUND_IDS.has(id);
        const shouldBlockAmbientSound = (id) => disableAmbientAudio.value && isAmbientSound(id);

        const pauseAmbientSounds = () => {
            if (!disableAmbientAudio.value) return;

            AMBIENT_SOUND_IDS.forEach((soundId) => {
                const sound = sounds[soundId];
                if (!sound) return;

                sound.fadeDelay?.kill();
                sound.pause();
            });
        };

        // Tap Howler.masterGain into a MediaStreamAudioDestinationNode so a
        // same-origin parent (e.g. UST seller broadcast) can read the resulting
        // MediaStream off `iframe.contentWindow.__odysseyBroadcastAudio` and
        // mix it with the seller's mic in its own AudioContext. The destination
        // works on suspended contexts too — it just produces silence until the
        // context resumes via unlockAudio.
        //
        // Opt-in via the `enableBroadcastAudio=true` query param. Default OFF —
        // when absent, this whole block is a no-op so the rest of Odyssey
        // behaves identically to pre-broadcast-tap behaviour.
        let broadcastDestination = null;
        const enableBroadcastAudio =
            typeof window !== 'undefined' &&
            new URLSearchParams(window.location.search).get(
                'enableBroadcastAudio'
            ) === 'true';
        if (enableBroadcastAudio && Howler.ctx && Howler.masterGain) {
            broadcastDestination = Howler.ctx.createMediaStreamDestination();
            Howler.masterGain.connect(broadcastDestination);
            window.__odysseyBroadcastAudio = broadcastDestination.stream;
        }

        const play = (
            id,
            { fade = false, unique = true, ignoreUnlocked = false, pitch, volume = null } = {}
        ) => {
            if (shouldBlockAmbientSound(id)) return;
            if (unlocked.value || ignoreUnlocked) {
                const sound = sounds[id];
                if (!sound) {
                    logger.warn('Sound not found', {
                        event: 'audio.sound_missing',
                        soundId: id,
                        action: 'play',
                    });
                    return;
                }

                const currentVolume = sound.volume();
                const targetVolume = volume ?? sound.baseVolume;

                sound.fadeDelay?.kill();

                if (fade) {
                    const duration = 1000;
                    sound.fade(currentVolume, targetVolume, duration);
                } else {
                    sound.volume(targetVolume);
                }

                sound.rate(pitch ?? sound.baseRate);

                if (!sound.playing() || unique) {
                    sound.play();
                }
            }
        };

        const pause = (id, { fade = false } = {}) => {
            if (unlocked.value) {
                const sound = sounds[id];
                if (!sound) {
                    logger.warn('Sound not found', {
                        event: 'audio.sound_missing',
                        soundId: id,
                        action: 'pause',
                    });
                    return;
                }

                const volume = sound.volume();

                sound.fadeDelay?.kill();

                if (fade) {
                    const duration = 1;
                    sound.fade(volume, 0, duration * 1000);
                    sound.fadeDelay = gsap.delayedCall(duration, () => {
                        sound.pause();
                    });
                } else {
                    sound.pause();
                }
            }
        };

        const playLoop = (id) => {
            // Don't play new loop sounds if tab is not active, unless explicitly told to ignore tab visibility

            if (shouldBlockAmbientSound(id)) return;

            if (unlocked.value) {
                const sound = sounds[id];
                if (!sound) {
                    logger.warn('Sound not found', {
                        event: 'audio.sound_missing',
                        soundId: id,
                        action: 'play_loop',
                    });
                    return;
                }

                sound.play('loop');
            }
        };

        // Function to set the global muted state
        const setMuted = (value) => {
            muted.value = !!value;
            if (!muted.value) pauseAmbientSounds();
        };

        const parseAudioEnabledState = (eventData) => {
            const enabled = eventData?.data?.enabled;
            if (typeof enabled === 'boolean') return enabled;

            return null;
        };

        useWindowMessage({
            onReceiveMessage: (event) => {
                if (event.data.type === UnlockAudio) {
                    unlockAudio(true);
                    return;
                }

                if (event.data.type === SetAudioEnabled) {
                    const enabled = parseAudioEnabledState(event.data);

                    if (enabled === null) {
                        logger.warn('SetAudioEnabled payload invalid, ignoring message', {
                            event: 'audio.set_audio_enabled_payload_invalid',
                            source: 'window_message',
                            payload: event.data,
                        });
                        return;
                    }

                    setMuted(!enabled);
                }
            },
        });

        const providerData = {
            volume,
            muted,
            sounds,
            play,
            pause,
            playLoop,
            disableAmbientAudio,

            setMuted,

            get unlocked() {
                return unlocked.value;
            },
        };

        watchEffect(() => {
            Howler.volume(volume.value);
            Howler.mute(muted.value);
        });

        provide(AudioKey, providerData);

        // After sounds are defined, set up visibility change listener
        onMounted(() => {
            // Add visibility change listener
            document.body.addEventListener('click', () => unlockAudio(true), { once: true });

            tabActive.value = !document.hidden;

            document.addEventListener('visibilitychange', handleVisibilityChange);
            interval = setInterval(() => unlockAudio(), 1000);
        });

        onUnmounted(() => {
            // Clean up visibility change listener
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearInterval(interval);

            if (broadcastDestination) {
                try {
                    Howler.masterGain?.disconnect(broadcastDestination);
                } catch {
                    // disconnect throws if not actually connected; ignore
                }
                broadcastDestination.stream.getTracks().forEach((t) => t.stop());
                broadcastDestination = null;
            }
            if (window.__odysseyBroadcastAudio) {
                delete window.__odysseyBroadcastAudio;
            }
        });
    },

    render() {
        if (this.$slots.default) {
            return this.$slots.default();
        }

        return null;
    },
});

export default AudioProvider;
